import i18next from "i18next";
import isEqual from "lodash/isEqual";
import type { AppDispatch, RootState } from "src/common/state/store";
import { composeMergePatches } from "src/common/util/applyMergePatch";
import { getUserPreferences } from "src/common/util/getUserPreferences";
import { setAlert } from "src/features/alert/state/alertsSlice";
import type { IUserPreferences } from "src/model/model";
import type { IUserPreferencesPatch } from "./profileSlice";
import {
  applyPreferencesPatch,
  fetchUserPreferences,
  patchUserPreferences,
  rememberPreferences,
  setPreferences,
} from "./profileSlice";

let tail: Promise<unknown> = Promise.resolve();
let queued: IUserPreferencesPatch | null = null;
let queuedWaiters: ((saved: boolean) => void)[] = [];
let sending = false;
let revision = 0;
let confirmed: IUserPreferences = {};

const readPreferences = (dispatch: AppDispatch): IUserPreferences =>
  dispatch(
    (_dispatch: AppDispatch, getState: () => RootState) =>
      getUserPreferences({ currentUser: getState().profile.currentUser }) ?? {},
  );

const schedule = <T>(task: () => Promise<T>): Promise<T> => {
  const run = tail.then(task);
  tail = run.catch(() => undefined);
  return run;
};

const applyServerPreferences = (dispatch: AppDispatch, preferences: IUserPreferences) => {
  rememberPreferences(preferences);
  dispatch(setPreferences(preferences));
  if (queued) {
    dispatch(applyPreferencesPatch(queued));
  }
};

const resync = async (dispatch: AppDispatch) => {
  try {
    const preferences = await dispatch(fetchUserPreferences({ resync: true })).unwrap();
    applyServerPreferences(dispatch, preferences);
  } catch (error) {
    console.warn("[preferencesWriter] failed to resync preferences", error);
  }
};

const sendQueued = async (dispatch: AppDispatch) => {
  const patch = queued;
  const waiters = queuedWaiters;
  queued = null;
  queuedWaiters = [];
  if (!patch) return;
  sending = true;
  try {
    confirmed = await dispatch(patchUserPreferences(patch)).unwrap();
    if (queued) {
      dispatch(applyPreferencesPatch(queued));
    }
    waiters.forEach((resolve) => resolve(true));
  } catch (error) {
    waiters.forEach((resolve) => resolve(false));
    console.warn("[preferencesWriter] failed to save preferences", error);
    dispatch(setAlert({ type: "danger", msg: i18next.t("sidebar.preferencesSaveError") }));
    dispatch(setPreferences(confirmed));
    if (queued) {
      dispatch(applyPreferencesPatch(queued));
    }
    await resync(dispatch);
  } finally {
    sending = false;
  }
};

export const enqueuePreferencesPatch = (
  dispatch: AppDispatch,
  patch: IUserPreferencesPatch,
): Promise<boolean> => {
  revision += 1;
  if (!queued && !sending) {
    confirmed = readPreferences(dispatch);
  }
  dispatch(applyPreferencesPatch(patch));
  const saved = new Promise<boolean>((resolve) => {
    queuedWaiters.push(resolve);
  });
  if (queued) {
    queued = composeMergePatches(queued, patch);
    return saved;
  }
  queued = patch;
  void schedule(() => sendQueued(dispatch));
  return saved;
};

export const refreshPreferences = async (dispatch: AppDispatch): Promise<IUserPreferences> => {
  const startRevision = revision;
  const preferences = await dispatch(fetchUserPreferences({})).unwrap();
  if (
    revision === startRevision &&
    !sending &&
    !queued &&
    !isEqual(preferences, readPreferences(dispatch))
  ) {
    applyServerPreferences(dispatch, preferences);
  }
  return preferences;
};

export const whenPreferencesSettled = () => tail.then(() => undefined);

export const resetPreferencesWriterForTests = () => {
  tail = Promise.resolve();
  queued = null;
  queuedWaiters = [];
  sending = false;
  revision = 0;
  confirmed = {};
};
